import {
	DestroyRef,
	Directive,
	effect,
	ElementRef,
	HostListener,
	inject,
	input,
	OnDestroy,
	output,
	signal,
	WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PlatformService } from 'lib/platform/index.js';
import { ScreenService } from 'lib/screen/index.js';
import { AppStableService } from 'lib/services/app-stable.service.js';
import { map, Observable, Subscription, throttleTime } from 'rxjs';
import tippy, { Instance, Placement, Tippy } from 'tippy.js';
import { TooltipMenuComponent } from './tooltip-menu.component.js';

export type TooltipTheme = 'default';
export type TooltipTrigger = 'click' | 'hover';

@Directive({
	selector: '[libTooltipMenu]',
	standalone: false
})
export class TooltipMenuDirective implements OnDestroy {
	public readonly libTooltipMenu = input.required<TooltipMenuComponent>();
	public readonly libTooltipMenuTriggerElement = input<ElementRef>();
	public readonly libTooltipMenuTrigger = input<TooltipTrigger>();
	public readonly libTooltipMenuMaxWidth = input<number>(550);
	public readonly libTooltipMenuPlacement = input<Placement>('auto');

	public readonly menuOpened = output<void>();

	private readonly template: WritableSignal<TooltipMenuComponent | undefined> = signal(undefined);

	private tippyInstances?: Instance<any>[] | Instance<any>;
	private destroyRef = inject(DestroyRef);

	private readonly isMobileView: boolean;
	private readonly canUseTippy: boolean;
	private isOpened: boolean = false;

	@HostListener('click', ['$event'])
	onClick(event: Event) {
		if (this.isOpened) {
			this.hideTippy();
			return;
		}

		if (!this.canUseTippy) {
			return;
		}

		this.isOpened = true;

		event.preventDefault();
		event.stopPropagation();

		/**
		 * This is using setTimeout because otherwise there was an issue when on opening dialog and then opening tooltipMenu = nothing happened
		 * E.g. when on clients -> Nutrition page. Click on "Select schedule" -> close dialog -> Click on "Page section selector" -> nothing happened
		 * Subsequent clicks worked just fine
		 */
		setTimeout(() => {
			if (this.tippyInstances) {
				if (Array.isArray(this.tippyInstances)) {
					for (const instance of this.tippyInstances) {
						instance.show();
						this.menuOpened.emit();
					}
				} else {
					this.tippyInstances.show();
					this.menuOpened.emit();
				}
			}
		}, 0);
	}

	constructor(
		private readonly hostElement: ElementRef,
		readonly screenService: ScreenService,
		readonly platformService: PlatformService,
		private readonly appStableService: AppStableService
	) {
		this.isMobileView = screenService.currentScreen().isMobileView;
		this.canUseTippy = platformService.isBrowser;

		effect(() => {
			if (!this.canUseTippy) {
				return;
			}

			const template = this.libTooltipMenu();
			const maxWidth = this.libTooltipMenuMaxWidth();
			this.template.set(template);

			this.destroyTippy();

			if (template) {
				this.tippyInstances = (tippy as any as Tippy)(this.hostElement.nativeElement, {
					content: '',
					allowHTML: true,
					appendTo: () => document.body,
					trigger: this.getTooltipTrigger(),
					duration: [75, 75],
					maxWidth: maxWidth,
					animation: 'scale',
					interactive: true,
					hideOnClick: true,
					theme: 'coachwayMenu',
					placement: this.libTooltipMenuPlacement(),
					arrow: true,
					onHidden: (instance) => {
						this.isOpened = false;
					}
				});

				// subscribe to content only after app is stable
				this.subscribeToObservable(
					this.appStableService.appIsStable_.pipe(
						map((stable) => {
							if (stable) {
								this.subscribeToContentLoaded(template);
							}
						})
					)
				);
			}
		});
	}

	ngOnDestroy(): void {
		this.destroyTippy();
	}

	private hideTippy(): void {
		this.isOpened = false;

		if (this.tippyInstances) {
			if (Array.isArray(this.tippyInstances)) {
				for (const instance of this.tippyInstances) {
					instance.hide();
				}
			} else {
				this.tippyInstances.hide();
			}
		}
	}

	private destroyTippy(): void {
		if (!this.canUseTippy) {
			return;
		}

		if (this.tippyInstances) {
			if (Array.isArray(this.tippyInstances)) {
				for (const instance of this.tippyInstances) {
					instance.destroy();
				}
			} else {
				this.tippyInstances.destroy();
			}
		}

		this.tippyInstances = undefined;
	}

	private getTooltipTrigger(): 'manual' | undefined {
		if (this.isMobileView) {
			return 'manual';
		}

		if (this.libTooltipMenuTrigger() === 'click' || !this.libTooltipMenuTrigger()) {
			return 'manual';
		}

		return undefined;
	}

	private subscribeToContentLoaded(template: TooltipMenuComponent): void {
		this.subscribeToObservable(
			template.contentLoaded_.pipe(
				// throttle time to prevent too many updates (re-renders trigger the 'contentLoaded')
				throttleTime(500, undefined, { trailing: true, leading: true }),
				map((htmlElement) => {
					//update skippy instance with new content
					if (this.tippyInstances && htmlElement) {
						if (Array.isArray(this.tippyInstances)) {
							for (const instance of this.tippyInstances) {
								instance.setContent(htmlElement);
							}
						} else {
							this.tippyInstances.setContent(htmlElement);
						}
					}
				})
			)
		);
	}

	private subscribeToObservable(observable: Observable<any>): Subscription {
		return observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}
}
